import React, { useMemo } from "react";
import { Link } from "@mui/material";
import { getBadgeType } from "@components/DataTable/columns";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import ErrorHandler from "@components/ErrorHandler/ErrorHandler";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import Subheader from "@components/Subheader/Subheader";
import type { FilterFieldDefinition } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import { getStatusInfo, statuses as vesselCallStatuses } from "@features/vessel-call/grid/utils";
import type { CargoOperationGridRow } from "@services/drybulkCargoOperationsApi";
import { paths } from "@utils/navigation";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { DRYBULK_CARGO_OPS_LANG_BASE, OPERATION_STATUSES } from "../constants";
import { useGradeOptions } from "../hooks/useGradeOptions";
import {
  GRID_PAGE_SIZE_OPTIONS,
  useOperationsQueueSearch,
} from "../hooks/useOperationsQueueSearch";
import { useQueueFilterOptions } from "../hooks/useQueueFilterOptions";
import { cargoOpsOperationPath } from "../utils/cargoOpsPaths";
import { formatGridInstant } from "../utils/formatInstant";
import { operationTypeOptions } from "../utils/operationTypeOptions";
import { operationStatusBadgeType } from "../utils/statusBadgeType";
import { statusLabel } from "../utils/statusLabel";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.queue`;
const NONE = "—";

const orNone = (value: string | null | undefined) =>
  value === null || value === undefined || value === "" ? NONE : value;

interface QueueRow {
  id: string;
  vesselImo: string;
  vesselCallId: string | null;
  vcn: string;
  operationTypeLabel: string;
  operationNumber: string;
  vesselType: string;
  flag: string;
  agencyName: string;
  lastPort: string;
  nextPort: string;
  arrivalLocation: string;
  atb: string;
  vesselCallStatusLabel: string;
  status: string | null;
  statusLabel: string;
}

const OperationsQueue: React.FC<{ pausePolling?: boolean }> = ({ pausePolling = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const grid = useOperationsQueueSearch({ pausePolling });
  const gradeOptions = useGradeOptions({ enabled: true });
  const filterOptions = useQueueFilterOptions();

  const headers: Header[] = useMemo(
    () => [
      {
        field: "vesselImo",
        columnName: t(`${L}.columns.imo`),
        flex: 1,
        minWidth: 110,
        renderCell: (row) => {
          const imo = String(row.vesselImo);
          const vesselCallId = row.vesselCallId as string | null;
          return vesselCallId && imo !== NONE ? (
            <Link
              component={RouterLink}
              underline="hover"
              to={`/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/${vesselCallId}`}
              onClick={(event) => event.stopPropagation()}
              data-testid={`operations-queue-imo-${row.id}`}
            >
              {imo}
            </Link>
          ) : (
            <span>{imo}</span>
          );
        },
      },
      { field: "vcn", columnName: t(`${L}.columns.vcn`), flex: 1, minWidth: 110 },
      {
        field: "operationTypeLabel",
        columnName: t(`${L}.columns.operation-type`),
        flex: 1,
        minWidth: 110,
      },
      {
        field: "operationNumber",
        columnName: t(`${L}.columns.operation-number`),
        flex: 1,
        minWidth: 110,
      },
      { field: "vesselType", columnName: t(`${L}.columns.vessel-type`), flex: 1, minWidth: 120 },
      { field: "flag", columnName: t(`${L}.columns.flag`), flex: 1, minWidth: 100 },
      {
        field: "agencyName",
        columnName: t(`${L}.columns.agency`),
        flex: 1.2,
        minWidth: 130,
      },
      { field: "lastPort", columnName: t(`${L}.columns.last-port`), flex: 1, minWidth: 110 },
      { field: "nextPort", columnName: t(`${L}.columns.next-port`), flex: 1, minWidth: 110 },
      {
        field: "arrivalLocation",
        columnName: t(`${L}.columns.arrival-location`),
        flex: 1.2,
        minWidth: 130,
      },
      { field: "atb", columnName: t(`${L}.columns.atb`), flex: 0.8, minWidth: 130 },
      {
        field: "vesselCallStatusLabel",
        columnName: t(`${L}.columns.vessel-call-status`),
        badgeType: (row) => getBadgeType(String(row.vesselCallStatusLabel)),
        flex: 1,
        minWidth: 210,
      },
      {
        field: "statusLabel",
        columnName: t(`${L}.columns.operation-status`),
        badgeType: (row) => operationStatusBadgeType(row.status as string),
        flex: 1.2,
        minWidth: 210,
      },
    ],
    [t],
  );

  const availableFields = useMemo<Record<string, FilterFieldDefinition>>(
    () => ({
      vesselImo: { label: t(`${L}.columns.imo`), type: "text" },
      operationNumber: { label: t(`${L}.columns.operation-number`), type: "text" },
      nominationRefOrderNumber: {
        label: t(`${DRYBULK_CARGO_OPS_LANG_BASE}.nomination-filter`),
        type: "text",
      },
      vcn: { label: t(`${L}.columns.vcn`), type: "text" },
      operationType: {
        label: t(`${L}.columns.operation-type`),
        type: "select",
        options: operationTypeOptions(t),
      },
      gradeSize: {
        label: t(`${L}.filter-grade-size`),
        type: "autocomplete",
        options: gradeOptions.options,
      },
      vesselType: {
        label: t(`${L}.columns.vessel-type`),
        type: "autocomplete",
        options: filterOptions.vesselTypes,
      },
      flag: { label: t(`${L}.columns.flag`), type: "autocomplete", options: filterOptions.flags },
      agency: {
        label: t(`${L}.columns.agency`),
        type: "autocomplete",
        options: filterOptions.agencies,
      },
      lastPort: {
        label: t(`${L}.columns.last-port`),
        type: "autocomplete",
        options: filterOptions.ports,
      },
      nextPort: {
        label: t(`${L}.columns.next-port`),
        type: "autocomplete",
        options: filterOptions.ports,
      },
      arrivalLocation: {
        label: t(`${L}.columns.arrival-location`),
        type: "autocomplete",
        options: filterOptions.locations,
      },
      vesselCallStatus: {
        label: t(`${L}.columns.vessel-call-status`),
        type: "autocomplete",
        options: vesselCallStatuses.map(({ value, label }) => ({
          value,
          label: t(`vessel-call.status.${label}`),
        })),
      },
      status: {
        label: t(`${L}.columns.operation-status`),
        type: "select",
        options: OPERATION_STATUSES.filter((status) => status !== "DRAFT").map((status) => ({
          label: statusLabel(t, status),
          value: status,
        })),
      },
    }),
    [t, gradeOptions.options, filterOptions],
  );

  const rows: QueueRow[] = grid.rows.map((row: CargoOperationGridRow) => ({
    id: row.id,
    vesselImo: orNone(row.vesselImo),
    vesselCallId: row.vesselCallId ?? null,
    vcn: orNone(row.vcn),
    operationTypeLabel: row.operationType
      ? t(
          `${DRYBULK_CARGO_OPS_LANG_BASE}.add.operation-type-${
            row.operationType === "LOAD" ? "loading" : "unloading"
          }`,
        )
      : NONE,
    operationNumber:
      row.operationNumber === null || row.operationNumber === undefined
        ? NONE
        : String(row.operationNumber),
    vesselType: orNone(row.vesselType),
    flag: orNone(row.flag),
    agencyName: orNone(row.requestingAgencyName),
    lastPort: orNone(row.lastPort),
    nextPort: orNone(row.nextPort),
    arrivalLocation: orNone(row.arrivalLocation),
    atb: formatGridInstant(row.atb, NONE),
    vesselCallStatusLabel: row.vesselCallStatus ? t(getStatusInfo(row.vesselCallStatus)) : NONE,
    status: row.status,
    statusLabel: statusLabel(t, row.status),
  }));

  return (
    <>
      <Subheader testId="operations-queue-subheader" title={t(`${L}.page-title`)} />
      <div data-testid="operations-queue" className="relative mt-8 px-24">
        {grid.isError ? (
          <ErrorHandler error={grid.error} testId="operations-queue-error" />
        ) : (
          <>
            <LoadingOverlay loading={grid.isLoading} testId="operations-queue-loading" />
            <CustomDataTable
              testId="operations-queue-grid"
              title={t(`${L}.table-title`)}
              actionName=""
              showHeaderWhenEmpty
              customEmptyMessagePrefix={`${L}.empty`}
              headers={headers}
              rows={rows}
              page={grid.page}
              pageSize={grid.pageSize}
              totalCount={grid.totalRecordCount}
              onPageChange={grid.changePage}
              onRowClick={(row) => navigate(cargoOpsOperationPath(row))}
              navigationLinkBase=""
              filters={grid.filters}
              onApplyFilters={grid.applyFilters}
              onClearFilters={grid.clearFilters}
              availableFields={availableFields}
              pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>
    </>
  );
};

export default OperationsQueue;
